import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePrivy } from "@privy-io/react-auth";
import type { WorkspaceSummary } from "@crewledger/shared/workspace";
import type { AccessGateState, PostLoginPath } from "@crewledger/shared/beta-access";
import { apiFetch } from "@/lib/api";
import { E2eAuthContext } from "@/features/auth/e2e-auth-provider";

export interface SessionUser {
  id: string;
  companyId: string;
  name: string;
  email: string;
  role: string;
  siteIds: string[] | null;
}

interface AuthContextValue {
  ready: boolean;
  authenticated: boolean;
  /** Whether the API accepted the Privy access token */
  serverAuthenticated: boolean;
  provisioned: boolean;
  user: SessionUser | null;
  workspace: WorkspaceSummary | null;
  redirectTo: PostLoginPath | null;
  accessGate: AccessGateState | null;
  email: string | null;
  getStartedEnabled: boolean;
  sessionMessage: string | null;
  sessionError: string | null;
  login: () => void;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { ready, authenticated, login, logout, getAccessToken } = usePrivy();
  const getAccessTokenRef = useRef(getAccessToken);
  getAccessTokenRef.current = getAccessToken;
  const [user, setUser] = useState<SessionUser | null>(null);
  const [workspace, setWorkspace] = useState<WorkspaceSummary | null>(null);
  const [redirectTo, setRedirectTo] = useState<PostLoginPath | null>(null);
  const [accessGate, setAccessGate] = useState<AccessGateState | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [getStartedEnabled, setGetStartedEnabled] = useState(false);
  const [provisioned, setProvisioned] = useState(false);
  const [serverAuthenticated, setServerAuthenticated] = useState(false);
  const [sessionMessage, setSessionMessage] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState(false);

  const refreshSession = useCallback(async () => {
    if (!authenticated) {
      setUser(null);
      setWorkspace(null);
      setRedirectTo(null);
      setAccessGate(null);
      setEmail(null);
      setGetStartedEnabled(false);
      setProvisioned(false);
      setServerAuthenticated(false);
      setSessionMessage(null);
      setSessionError(null);
      setSessionReady(true);
      return;
    }

    setSessionReady(false);
    setSessionError(null);

    try {
      // Read via ref so unstable Privy callback identities cannot retrigger session loops.
      const token = await getAccessTokenRef.current();
      if (!token) {
        setServerAuthenticated(false);
        setProvisioned(false);
        setUser(null);
        setSessionMessage(null);
        setSessionError("Could not obtain an access token. Try signing in again.");
        return;
      }

      const response = await apiFetch<{
        data: {
          authenticated: boolean;
          provisioned: boolean;
          getStartedEnabled?: boolean;
          email?: string | null;
          redirectTo?: PostLoginPath;
          accessGate?: AccessGateState | null;
          user?: SessionUser;
          workspace?: WorkspaceSummary | null;
          message?: string;
        };
      }>("/api/auth/session", {
        headers: { Authorization: `Bearer ${token}` },
      });

      setServerAuthenticated(Boolean(response.data.authenticated));
      setProvisioned(Boolean(response.data.provisioned));
      setGetStartedEnabled(Boolean(response.data.getStartedEnabled));
      setEmail(response.data.email ?? null);
      setRedirectTo(response.data.redirectTo ?? null);
      setAccessGate(response.data.accessGate ?? null);
      setUser(response.data.user ?? null);
      setWorkspace(response.data.workspace ?? null);
      setSessionMessage(response.data.message ?? null);
    } catch (error) {
      setServerAuthenticated(false);
      setProvisioned(false);
      setUser(null);
      setWorkspace(null);
      setRedirectTo(null);
      setAccessGate(null);
      setEmail(null);
      setGetStartedEnabled(false);
      setSessionMessage(null);
      setSessionError(
        error instanceof Error
          ? error.message
          : "Could not reach the server. Make sure the API is running (npm run dev).",
      );
    } finally {
      setSessionReady(true);
    }
  }, [authenticated]);

  useEffect(() => {
    if (ready) {
      void refreshSession();
    }
  }, [ready, authenticated, refreshSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ready: ready && sessionReady,
      authenticated,
      serverAuthenticated,
      provisioned,
      user,
      workspace,
      redirectTo,
      accessGate,
      email,
      getStartedEnabled,
      sessionMessage,
      sessionError,
      login,
      logout: async () => {
        await logout();
        setUser(null);
        setWorkspace(null);
        setGetStartedEnabled(false);
        setProvisioned(false);
        setServerAuthenticated(false);
        setSessionMessage(null);
        setSessionError(null);
      },
      getAccessToken,
      refreshSession,
    }),
    [
      ready,
      sessionReady,
      authenticated,
      serverAuthenticated,
      provisioned,
      user,
      workspace,
      redirectTo,
      accessGate,
      email,
      getStartedEnabled,
      sessionMessage,
      sessionError,
      login,
      logout,
      getAccessToken,
      refreshSession,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const e2eCtx = useContext(E2eAuthContext);
  const ctx = useContext(AuthContext);
  if (import.meta.env.VITE_E2E_AUTH_BYPASS === "true") {
    if (!e2eCtx) {
      throw new Error("useAuth must be used within E2eAuthProvider in E2E mode");
    }
    return e2eCtx;
  }
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
